import {
  ArrayInput,
  BooleanField,
  BooleanInput,
  Create,
  Datagrid,
  DateField,
  DateInput,
  Edit,
  List,
  ReferenceField,
  ReferenceInput,
  SelectField,
  SelectInput,
  Show,
  SimpleForm,
  SimpleFormIterator,
  SimpleShowLayout,
  TextField,
  TextInput,
} from "react-admin";
import { InfoLabel } from "@fluentui/react-components";
import {
  CandidateOpportunityProfileField,
  StringArrayField,
} from "./CustomFields";
import jobCandidateStatusToLabel from "@/utils/jobCandidateStatusToLabels";
import { candidateApplicationStatusList } from "../../types";

const statusChoices = candidateApplicationStatusList.map((type) => ({
  id: type,
  name: jobCandidateStatusToLabel(type),
}));

const filters = [
  <ReferenceInput key="candidate" source="candidate" reference="candidates" />,
  <ReferenceInput
    key="job_opening"
    source="job_opening"
    reference="job_openings"
  />,
  <SelectInput key="status" source="status" choices={statusChoices} />,
  <BooleanInput key="agrees_with_scope" source="agrees_with_scope" />,
  <TextInput key="scope" source="scope" />,
  <BooleanInput key="agrees_with_budget" source="agrees_with_budget" />,
  <TextInput key="budget_hourly" source="budget_hourly" />,
  <TextInput key="budget_monthly" source="budget_monthly" />,
  <BooleanInput key="agrees_with_start_date" source="agrees_with_start_date" />,
  <DateInput key="start_date" source="start_date" />,
  <TextInput key="opt_out_reasons" source="opt_out_reasons" />,
  <DateInput key="opt_in_date" source="opt_in_date" />,
  <DateInput key="opt_out_date" source="opt_out_date" />,
  <TextInput key="rejection_reasons" source="rejection_reasons" />,
  <DateInput key="rejection_date" source="rejection_date" />,
  <DateInput key="created_at" source="created_at" />,
];

export const CandidateStatusList = () => (
  <List filters={filters}>
    <Datagrid>
      <ReferenceField source="candidate" reference="candidates" />
      <ReferenceField source="job_opening" reference="job_openings" />
      <CandidateOpportunityProfileField />
      <SelectField source="status" choices={statusChoices} />
      <BooleanField source="agrees_with_scope" />
      <TextField source="scope" />
      <BooleanField source="agrees_with_budget" />
      <TextField source="budget_hourly" />
      <TextField source="budget_monthly" />
      <BooleanField source="agrees_with_start_date" />
      <DateField source="start_date" />
      <BooleanField source="opt_in_requested" />
      <TextField source="opt_out_reasons" />
      <DateField source="opt_in_date" />
      <DateField source="opt_out_date" />
      <TextField source="rejection_reasons" />
      <DateField source="rejection_date" />
      <DateField source="created_at" />
    </Datagrid>
  </List>
);

export const CandidateStatusShow = () => (
  <Show>
    <SimpleShowLayout>
      <TextField source="id" />
      <ReferenceField source="candidate" reference="candidates" />
      <ReferenceField source="job_opening" reference="job_openings" />
      <CandidateOpportunityProfileField />
      <SelectField source="status" choices={statusChoices} />
      <BooleanField source="agrees_with_scope" />
      <TextField source="scope" />
      <BooleanField source="agrees_with_budget" />
      <TextField source="budget_hourly" />
      <TextField source="budget_monthly" />
      <BooleanField source="agrees_with_start_date" />
      <DateField source="start_date" />
      <StringArrayField source="match_criteria" />
      <StringArrayField source="match_criteria_suggestions" />
      <StringArrayField source="match_criteria_quotes" />
      <TextField source="personality_role_strength" />
      <TextField source="personality_role_strength_detail" />
      <TextField source="behavior_role_strength" />
      <TextField source="behavior_role_strength_detail" />
      <TextField source="motivator_role_strength" />
      <TextField source="motivator_role_strength_detail" />
      <BooleanField source="opt_in_requested" />
      <TextField source="opt_out_reasons" />
      <DateField source="opt_in_date" />
      <DateField source="opt_out_date" />
      <TextField source="rejection_reasons" />
      <DateField source="rejection_date" />
      <DateField source="created_at" />
    </SimpleShowLayout>
  </Show>
);

export const CandidateStatusCreate = () => (
  <Create>
    <SimpleForm>
      <ReferenceInput source="candidate" reference="candidates" />
      <ReferenceInput source="job_opening" reference="job_openings" />
      <InfoLabel
        className="font-semibold"
        info={
          <ul>
            <li>
              <strong>Identified:</strong> Hidden
            </li>
            <li>
              <strong>Initial Outreach:</strong> Hidden
            </li>
            <li>
              <strong>Checking Interest:</strong> Emails candidate
            </li>
            <li>
              <strong>Screening:</strong> Candidate opted-in
            </li>
            <li>
              <strong>Possible Fit:</strong> No change
            </li>
            <li>
              <strong>Shortlisted:</strong> Visible to company
            </li>
            <li>
              <strong>Making Offer:</strong> No change
            </li>
            <li>
              <strong>Contracting:</strong> Offer accepted, contract offline
            </li>
            <li>
              <strong>Accepted by Client:</strong> Candidate selected
            </li>
            <li>
              <strong>Rejected for Role:</strong> Company passed
            </li>
            <li>
              <strong>Not Interested:</strong> Candidate passed
            </li>
          </ul>
        }
      >
        Status Overview
      </InfoLabel>
      <SelectInput source="status" choices={statusChoices} />
      <BooleanInput source="agrees_with_scope" />
      <TextInput source="scope" />
      <BooleanInput source="agrees_with_budget" />
      <TextInput source="budget_hourly" />
      <TextInput source="budget_monthly" />
      <BooleanInput source="agrees_with_start_date" />
      <DateInput source="start_date" />
      <ArrayInput source="match_criteria">
        <SimpleFormIterator>
          <TextInput source="" multiline />
        </SimpleFormIterator>
      </ArrayInput>
      <ArrayInput source="match_criteria_suggestions">
        <SimpleFormIterator>
          <TextInput source="" multiline />
        </SimpleFormIterator>
      </ArrayInput>
      <ArrayInput source="match_criteria_quotes">
        <SimpleFormIterator>
          <TextInput source="" multiline />
        </SimpleFormIterator>
      </ArrayInput>
      <TextInput source="personality_role_strength" />
      <TextInput source="personality_role_strength_detail" multiline />
      <TextInput source="behavior_role_strength" />
      <TextInput source="behavior_role_strength_detail" multiline />
      <TextInput source="motivator_role_strength" />
      <TextInput source="motivator_role_strength_detail" multiline />
      <span>
        If selected, will not send any additional emails asking the candidate to
        opt-in
      </span>
      <BooleanInput source="opt_in_requested" />
      <TextInput source="opt_out_reasons" />
      <DateInput source="opt_in_date" />
      <DateInput source="opt_out_date" />
      <TextInput source="rejection_reasons" />
      <DateInput source="rejection_date" />
      <DateInput source="created_at" />
    </SimpleForm>
  </Create>
);

export const CandidateStatusEdit = () => (
  <Edit>
    <SimpleForm>
      <ReferenceInput source="candidate" reference="candidates" />
      <ReferenceInput source="job_opening" reference="job_openings" />
      <InfoLabel
        className="font-semibold"
        info={
          <ul>
            <li>
              <strong>Identified:</strong> Hidden
            </li>
            <li>
              <strong>Initial Outreach:</strong> Hidden
            </li>
            <li>
              <strong>Checking Interest:</strong> Emails candidate
            </li>
            <li>
              <strong>Screening:</strong> Candidate opted-in
            </li>
            <li>
              <strong>Possible Fit:</strong> No change
            </li>
            <li>
              <strong>Shortlisted:</strong> Visible to company
            </li>
            <li>
              <strong>Making Offer:</strong> No change
            </li>
            <li>
              <strong>Contracting:</strong> Offer accepted, contract offline
            </li>
            <li>
              <strong>Accepted by Client:</strong> Candidate selected
            </li>
            <li>
              <strong>Rejected for Role:</strong> Company passed
            </li>
            <li>
              <strong>Not Interested:</strong> Candidate passed
            </li>
          </ul>
        }
      >
        Status Overview
      </InfoLabel>
      <SelectInput source="status" choices={statusChoices} />
      <BooleanInput source="agrees_with_scope" />
      <TextInput source="scope" />
      <BooleanInput source="agrees_with_budget" />
      <TextInput source="budget_hourly" />
      <TextInput source="budget_monthly" />
      <BooleanInput source="agrees_with_start_date" />
      <DateInput source="start_date" />
      <ArrayInput source="match_criteria">
        <SimpleFormIterator>
          <TextInput source="" multiline />
        </SimpleFormIterator>
      </ArrayInput>
      <ArrayInput source="match_criteria_suggestions">
        <SimpleFormIterator>
          <TextInput source="" multiline />
        </SimpleFormIterator>
      </ArrayInput>
      <ArrayInput source="match_criteria_quotes">
        <SimpleFormIterator>
          <TextInput source="" multiline />
        </SimpleFormIterator>
      </ArrayInput>
      <TextInput source="personality_role_strength" />
      <TextInput source="personality_role_strength_detail" multiline />
      <TextInput source="behavior_role_strength" />
      <TextInput source="behavior_role_strength_detail" multiline />
      <TextInput source="motivator_role_strength" />
      <TextInput source="motivator_role_strength_detail" multiline />
      <span>
        If selected, will not send any additional emails asking the candidate to
        opt-in
      </span>
      <BooleanInput source="opt_in_requested" />
      <TextInput source="opt_out_reasons" />
      <DateInput source="opt_in_date" />
      <DateInput source="opt_out_date" />
      <TextInput source="rejection_reasons" />
      <DateInput source="rejection_date" />
      <DateInput source="created_at" />
    </SimpleForm>
  </Edit>
);
